import React, { useState, useMemo, useEffect, useCallback } from "react"
import { useParams, useHistory, useLocation } from "react-router-dom"
import getSelectionsFromParams from "../utils/getSelectionsFromParams"
import classnames from "classnames"

import Map from "../Map/"
import { getCountryByName } from "../utils/countryLookup"
import { GradientLegend } from "../Legends/"
import DataLoader, { years, factors } from "../DataLoader"
import "./styles.scss"
// import FactorInfoIconTooltip from './FactorInfoIconTooltip'
import CountrySelect, { unselectedCountryLabel } from "../CountrySelect/"
import useDimensions from "react-use-dimensions"
import { useWindowScroll, useScrollbarWidth } from "react-use"
import classNames from "classnames"
const defaultEmptyRegionSelection = []

const mapStrokeOutline = { color: "#333", width: 1.5 }
const setOverrideMapVisibility = () => {}

export default function InsightsView(props) {
  const [mapRef, mapDimensions] = useDimensions()
  const params = useParams()
  const { selectedCountry, selectedYear, selectedFactor, unusedOpts } =
    getSelectionsFromParams(params)
  const history = useHistory()
  const location = useLocation()
  // const [hoveredOverFactor, setHoveredOverFactor] = useState(null)
  const { children, ...otherProps } = props
  const windowScroll = useWindowScroll()

  const updateUrlFromEvent = (property) => {
    return (event) => {
      updateUrl(property)(event.target.value)
    }
  }
  const updateUrl = useCallback(
    (property) => {
      return (newValue) => {
        const newUrlParams = {
          country: selectedCountry,
          year: selectedYear,
          factor: selectedFactor,
        }
        newUrlParams[property] = newValue
        if (property === "country" && newValue === unselectedCountryLabel) {
          newUrlParams[property] = ""
        }

        let baseUrl = null
        switch (true) {
          case !!location.pathname.match("/global"):
            baseUrl = "/global"
            break
          case !!location.pathname.match("/country"):
            baseUrl = "/country"
            break
          case !!location.pathname.match("/factors"):
            baseUrl = "/factors"
            break
          default:
            console.warn("something went wrong")
            break
        }
        if (!baseUrl) {
          console.warn("something went wrong")
          return
        }
        let url = `${baseUrl}/${encodeURIComponent(newUrlParams.year)}`
        if (newUrlParams.country !== "") {
          url = `${url}/${encodeURIComponent(newUrlParams.country)}`
        }
        if (newUrlParams.factor !== "") {
          url = `${url}/${encodeURIComponent(newUrlParams.factor)}`
        }
        if (unusedOpts) {
          url = `${url}/${unusedOpts.join("/")}`
        }
        history.push(url)
      }
    },
    [
      history,
      location,
      selectedCountry,
      selectedYear,
      selectedFactor,
      unusedOpts,
    ],
  )

  const gotoCountryPage = useCallback(
    (country) => {
      return () => {
        const newUrlParams = {
          country: country,
          year: selectedYear,
          factor: selectedFactor,
        }

        const baseUrl = "/country"
        let url = `${baseUrl}/${encodeURIComponent(newUrlParams.year)}`
        if (newUrlParams.country !== "") {
          url = `${url}/${encodeURIComponent(newUrlParams.country)}`
        }
        if (newUrlParams.factor !== "") {
          url = `${url}/${encodeURIComponent(newUrlParams.factor)}`
        }
        history.push(url)
      }
    },
    [selectedYear, selectedFactor, history],
  )

  const selectCountryFromMinimap = useCallback(
    (country) => {
      return () => {
        updateUrl("country")(country)
      }
    },
    [updateUrl],
  )

  const updateCountryFromAutocomplete = (country) => {
    updateUrl("country")(
      country.Country === unselectedCountryLabel ? "" : country.Country,
    )
  }
  const onGlobalInsights = !!location.pathname.match("/global")
  const onCountryInsights = !!location.pathname.match("/country")

  const [showGlobalInsightsInstructions, setShowGlobalInsightsInstructions] =
    useState(false)

  const [mainMapInteractable, setMainMapInteractable] = useState(false)

  useEffect(() => {
    let timeoutId = null
    if (onGlobalInsights && !showGlobalInsightsInstructions) {
      timeoutId = setTimeout(() => {
        setMainMapInteractable(true)
      }, 500)
    } else if (showGlobalInsightsInstructions) {
      setMainMapInteractable(false)
    }
    return () => {
      clearTimeout(timeoutId)
    }
  }, [onGlobalInsights, showGlobalInsightsInstructions])

  const toggleGlobalInsightsInstructions = useCallback(() => {
    setShowGlobalInsightsInstructions(!showGlobalInsightsInstructions)
  }, [showGlobalInsightsInstructions])

  const currentYearData = DataLoader.dataByYear[selectedYear]
  const factorKey = useMemo(() => {
    return selectedFactor === ""
      ? "overall"
      : currentYearData.aggregateFactorKeys.find((k) =>
          k.includes(selectedFactor),
        )
  }, [selectedFactor, currentYearData])

  const mapData = useMemo(
    () =>
      currentYearData.countries.map((c) => ({
        code: c["Country Code"],
        value: c[factorKey],
        originalDatum: c,
        scoreKey: factorKey,
      })),
    [currentYearData, factorKey],
  )
  let selectedRegion = useMemo(() => {
    if (selectedCountry) {
      return getCountryByName(selectedCountry)
        ? [getCountryByName(selectedCountry).id]
        : defaultEmptyRegionSelection
    }
    return defaultEmptyRegionSelection
  }, [selectedCountry])
  const miniMapWidth = mapDimensions.width
  const headerHeight = 111
  const miniMapHeight = isNaN(miniMapWidth / 2) ? 0 : miniMapWidth / 2

  const sidebarMap = miniMapWidth ? (
    <Map
      id="insights"
      width={miniMapWidth}
      height={miniMapHeight}
      data={mapData}
      selectedRegion={selectedRegion}
      strokedCountries={selectedRegion}
      customStroke={mapStrokeOutline}
      animate={true}
      gotoCountryPage={
        onCountryInsights ? gotoCountryPage : selectCountryFromMinimap
      }
      tooltipEnabled={false}
      allowZoomOut={true}
      selectedYear={selectedYear}
    />
  ) : null

  const scrollbarSize = useScrollbarWidth()

  const mapWidth = window.innerWidth - scrollbarSize
  const mobile = "ontouchstart" in window && mapWidth < 900

  const useSmallerMapHeight = location.pathname.match("/factors") && !mobile
  const mapMaxHeight = window.innerHeight - headerHeight - 200

  const mapSizeBasedOnWidth = useSmallerMapHeight
    ? window.innerWidth / 4
    : window.innerWidth / 2
  const fullscreenMapHeight = Math.min(mapMaxHeight, mapSizeBasedOnWidth)

  useEffect(() => {
    if (selectedCountry && onGlobalInsights) {
      window.scrollTo(0, fullscreenMapHeight + 72) // 72 is WJP header height
    }
  }, [selectedCountry, onGlobalInsights, fullscreenMapHeight])

  const { fade, setFade } = props
  const { current } = mapRef

  useEffect(() => {
    if (fade && current) {
      setTimeout(() => setFade(false), 350)
    }
  }, [fade, setFade, current])

  const factorOptions = ["", ...factors]

  const yearsToUse =
    selectedCountry === "" || !DataLoader.yearsForCountries[selectedCountry]
      ? years
      : DataLoader.yearsForCountries[selectedCountry]

  const yearSelectOptions = yearsToUse.map((year) => (
    <option value={year} key={year}>
      {year}
    </option>
  ))
  const factorSelectOptions = factorOptions.map((factor) => (
    <option value={factor} key={factor}>
      {factor === "" ? "Overall" : factor}
    </option>
  ))

  const naturalMapScale = Math.pow(
    Math.max(0, (fullscreenMapHeight - windowScroll.y) / fullscreenMapHeight),
    3,
  )
  let mapScale = naturalMapScale

  const mapHeight = fullscreenMapHeight * mapScale
  const showSelectCountryNotice = onCountryInsights && !selectedCountry
  const showMiniMap =
    mapHeight < miniMapHeight || (onCountryInsights && selectedCountry !== "")
  const displaySidebarMap =
    (onCountryInsights && selectedCountry !== "") || showMiniMap

  if (showMiniMap) {
    mapScale = 0
  }

  useEffect(() => {
    if (onGlobalInsights) {
      setShowGlobalInsightsInstructions(true)
    }
  }, [onGlobalInsights])

  useEffect(() => {
    const listItem = document.querySelector(".globalInsightInstructionsText li")
    if (listItem) {
      const listItemHeight = listItem.offsetHeight
      // eslint-disable-next-line react-hooks/exhaustive-deps
      helpItem2Offset = listItemHeight + 10 // Dynamically adjust based on content height
    }
  }, [showGlobalInsightsInstructions])

  const fullscreenMap =
    (onCountryInsights && selectedCountry) ||
    (mobile && onGlobalInsights) ? null : (
      <Map
        id={`insightsFull-${location.pathname.split("/")[1]}`}
        width={mapWidth}
        height={fullscreenMapHeight}
        data={mapData}
        selectedRegion={selectedRegion}
        strokedCountries={selectedRegion}
        customStroke={mapStrokeOutline}
        animate={true}
        gotoCountryPage={
          onCountryInsights ? gotoCountryPage : selectCountryFromMinimap
        }
        tooltipEnabled={!mobile}
        allowZoomOut={true}
        selectedYear={selectedYear}
        mapScale={mapScale}
        overflow={true}
      />
    )

  const instructionsStyle = {
    transform: `translate(${mapWidth / 2}px, ${
      fullscreenMapHeight * 0.25
    }px) translate(-50%, -50%)`,
    opacity: showGlobalInsightsInstructions ? 1 : 0.5,
  }

  const [itemPositions, totalBoundingBox] = useMemo(() => {
    const items = [
      ".overviewInstructionsFactors",
      ".overviewInstructionsHistorical",
      ".overviewInstructionsFilters",
    ]
      .map((selector) => document.querySelector(selector))
      .map((el) => (el ? el.getBoundingClientRect() : null))

    const boundingBox = items.reduce(
      (acc, item) => {
        if (!item) {
          return acc
        }
        return {
          left: Math.min(acc.left, item.left),
          right: Math.max(acc.right, item.right),
          top: Math.min(acc.top, item.top),
          bottom: Math.max(acc.bottom, item.bottom),
        }
      },
      {
        left: Infinity,
        right: -Infinity,
        top: Infinity,
        bottom: -Infinity,
      },
    )
    if (items.filter((d) => d).length === 0) {
      return [null, null]
    }
    return [items, boundingBox]
  }, [])

  let interactiveInstructionsStyle = {
    transform: `translate(${
      mapWidth / 2
    }px, ${fullscreenMapHeight}px) translateY(-1em) translate(-50%, -100%)`,
    opacity: showGlobalInsightsInstructions ? 1 : 0.5,
  }
  let helpItem2Offset = 40
  if (itemPositions) {
    const firstItemCenterX = itemPositions[0].left + itemPositions[0].width / 2
    interactiveInstructionsStyle.width =
      totalBoundingBox.right - totalBoundingBox.left + helpItem2Offset
    interactiveInstructionsStyle.transform = `translate(${firstItemCenterX}px, ${
      fullscreenMapHeight - 10
    }px) translateY(-100%) translateY(-1em)`
  }
  let innerInstructionsStyle = {}
  let innerInteractiveInstructionsStyle = {}

  if (!showGlobalInsightsInstructions) {
    const helpIconXOffset = 30
    const helpIconYOffset = 20
    instructionsStyle.transform += `translate(${
      mapWidth / 2 - helpIconXOffset
    }px, ${fullscreenMapHeight * 0.75 + helpIconYOffset}px) scale(0)`
    instructionsStyle.transform += `translate(${
      mapWidth / 2 - helpIconXOffset
    }px, 0px) scale(0)`
    innerInstructionsStyle = {
      transform: `translate(0, ${fullscreenMapHeight / 2 + helpIconYOffset}px)`,
      opacity: showGlobalInsightsInstructions ? 1 : 0,
    }

    interactiveInstructionsStyle.transform += `translate(${
      mapWidth / 2 - helpIconXOffset
    }px, ${helpIconYOffset}px) translateY(50%) scale(0)`
    interactiveInstructionsStyle.transform += `translate(${
      mapWidth / 2 - helpIconXOffset
    }px, 0px) scale(0)`
    innerInteractiveInstructionsStyle = {
      transform: `translate(0, ${helpIconYOffset}px)`,
      opacity: showGlobalInsightsInstructions ? 1 : 1,
    }
  }
  let gloalInsightsInstructionsPointerEvents = null
  if (!showGlobalInsightsInstructions && mainMapInteractable) {
    gloalInsightsInstructionsPointerEvents = "none"
  }
  if (!onGlobalInsights) {
    gloalInsightsInstructionsPointerEvents = "none"
  }
  if (mobile) {
    interactiveInstructionsStyle.display = "none"
    instructionsStyle.transform = null
    innerInstructionsStyle.transform = null
    if (!showGlobalInsightsInstructions) {
      instructionsStyle.marginTop = "-100%"
    }
    if (!onGlobalInsights) {
      instructionsStyle.display = "none"
    }
  }
  let globalInsightInstructions = (
    <div
      className={classNames("globalInsightInstructionsWrapper", { mobile })}
      onClick={(e) => setShowGlobalInsightsInstructions(false)}
      style={{ pointerEvents: gloalInsightsInstructionsPointerEvents }}
    >
      <div className="globalInsightInstructions" style={instructionsStyle}>
        <div className="inner" style={innerInstructionsStyle}>
          <div className="globalInsightInstructionsCloseButton">&times;</div>
          <div className="globalInsightInstructionsTitle">
            {mobile ? "Interactive" : "Country"} Information
          </div>
          <div className="globalInsightInstructionsText">
            {mobile
              ? "To view by factor specific rankings select a factor above. To view historical and regional rankings click on any of the areas below."
              : "Hover over any country on the map or the list below to view scores and rankings. To view country profiles, select the countries tab on the menu above."}
          </div>
        </div>
      </div>
      <div
        className="globalInsightInstructions interactiveInstructions"
        style={interactiveInstructionsStyle}
      >
        <div className="inner" style={innerInteractiveInstructionsStyle}>
          {/* <div className='globalInsightInstructionsCloseButton'>&times;</div> */}
          <div className="globalInsightInstructionsTitle">
            Interactive Information
          </div>
          <div className="globalInsightInstructionsText">
            <ul>
              <li>View rankings by specific factors. </li>
              <li>View historical rankings since 2015. </li>
              <li>View regional or income-based rankings. </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
  return (
    <div className={`InsightsView ${fade ? "fadeIn" : ""}`}>
      <div className="controls">
        <div className="control-section selects">
          <CountrySelect
            value={selectedCountry}
            onChange={updateCountryFromAutocomplete}
            selectedYear={selectedYear}
          />
          <select
            aria-label="Year"
            value={selectedYear}
            onChange={updateUrlFromEvent("year")}
          >
            {yearSelectOptions}
          </select>
        </div>
        <div className="control-section factors">
          <select
            aria-label="Factor"
            value={selectedFactor}
            onChange={updateUrlFromEvent("factor")}
          >
            {factorSelectOptions}
          </select>
        </div>
        <div className="control-section legend">
          <GradientLegend width={320} height={44} />
        </div>
      </div>
      <div
        style={{
          position: "relative",
          borderBottom: mobile ? null : "1px solid rgba(86, 86, 90, 0.3)",
          overflow: "hidden",
        }}
      >
        <div
          style={{ top: headerHeight, transform: `scale(${mapScale})` }}
          className="mapContainer"
        >
          {fullscreenMap}
        </div>
        {globalInsightInstructions}
      </div>
      {showSelectCountryNotice ? (
        <div className="noDataMessage">
          Please select a country to view insights
        </div>
      ) : (
        <div className="factorAndContentWrapper" style={{ top: headerHeight }}>
          <div className="factorsColumn overviewInstructionsFactors">
            <div
              ref={mapRef}
              className={classnames("map", { displaySidebarMap })}
            >
              {sidebarMap}
            </div>
            <div
              className="factorsSelector"
              style={{
                transform: showMiniMap
                  ? null
                  : `translateY(${-miniMapHeight}px)`,
              }}
            >
              {factorOptions.map((factor, factorIndex) => {
                const selected = factor === selectedFactor
                const label =
                  factorIndex === 0
                    ? "Overall Index Score"
                    : `${factorIndex} | ${factor}`
                const instructions =
                  factorIndex === 0 ? (
                    <div className="factorInstructions" key="instructions">
                      Explore by Issue/Factor
                    </div>
                  ) : null
                const arrow = selected ? <span className="arrow" /> : null
                return (
                  <React.Fragment key={factor}>
                    <div
                      key="label"
                      className={classnames("factorOption", { selected })}
                      onClick={() => updateUrl("factor")(factor)}
                    >
                      {label}
                    </div>
                    {arrow}
                    {instructions}
                  </React.Fragment>
                )
              })}
            </div>
          </div>
          <div className="contentWrapper">
            <div className="content">
              {React.cloneElement(children, {
                headerHeight: naturalMapScale === 0 ? headerHeight : null,
                toggleGlobalInsightsInstructions,
                selectedCountry,
                selectedYear,
                selectedFactor,
                unusedOpts,
                updateUrl,
                setOverrideMapVisibility,
                gotoCountryPage,
                ...otherProps,
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
